import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { Trash2, Eye, AlertTriangle, CheckCircle2, Loader2, Database, ShieldAlert, Building2, PackageMinus, FolderTree, Play, Warehouse, Search, Download, Users, Upload, ToggleLeft, ToggleRight } from "lucide-react";
import { toast, Toaster } from "sonner";
import { triggerMastersRefresh } from "@/hooks/useMastersRefresh";
import { ReportingPeriodMigrationTile } from "@/components/ReportingPeriodMigrationTile";

// Admin-only data hygiene tools. Currently houses the "Wipe Demo Masters"
// utility — a 1-click way to clear the seed sample records (Metro Phase III
// project, Delhi Metro client, Bharat Cables vendor, etc.) once the tenant
// has migrated to real data. Designed to be additive: drop new tiles into
// the grid below as more cleanup utilities are needed.

export default function DataToolsPage() {
  return (
    <div data-testid="data-tools-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Administration · Data Tools"
        title="Data Tools"
        subtitle="One-click utilities to clean up legacy / demo data and keep your production database tidy. Admin-only."
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ReportingPeriodMigrationTile />
        <PaymentIntegrityTile />
        <ReconciliationDetailTile />
        <WipeDemoMastersTile />
        <BranchTagNormalizeTile />
        <IvFromWarehouseBackfillTile />
        <BackfillVBProjectIdTile />
        <SiWarehouseRepairTile />
        <SiStockOutTile />
        <StockIntegrityAuditTile />
        <ProjectAccessAuditTile />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Sprint 2026-Jun — Payment Integrity tile (no-shell production).
// Two one-click maintenance actions, both idempotent:
//   1. Fix Duplicate Bill Payments — deletes safe Outstanding orphan
//      rows whose source_line_id no longer matches the voucher lines
//      (the "amounts showing twice" bug for approved General Payments).
//   2. Backfill Beneficiary Snapshots — freezes bank details onto
//      legacy BPVs / advances so batch bank-Excel exports unblock.
// ─────────────────────────────────────────────────────────────────
function PaymentIntegrityTile() {
  const [busy, setBusy] = useState(false);
  const [dupPreview, setDupPreview] = useState(null);
  const [dupResult, setDupResult] = useState(null);
  const [snapPreview, setSnapPreview] = useState(null);
  const [snapResult, setSnapResult] = useState(null);

  const dupTotals = (d) => {
    const mods = Object.values(d?.modules || {});
    return {
      deleted: mods.reduce((s, m) => s + (m.orphan_cleanup?.orphans_deleted || 0)
                                       + (m.same_key_dedupe?.dupes_deleted || 0), 0),
      refused: mods.reduce((s, m) => s + (m.orphan_cleanup?.orphans_refused || 0)
                                       + (m.same_key_dedupe?.dupes_refused || 0), 0),
      stamped: mods.reduce((s, m) => s + (m.line_id_backfill?.lines_stamped || 0), 0),
    };
  };

  async function runDup(dryRun) {
    if (!dryRun) {
      const t = dupTotals(dupPreview);
      if (!window.confirm(
        `Delete ${t.deleted} duplicate/orphan bill payment row(s)?\n\n` +
        `Only safe rows are removed (Outstanding, no deductions/holds, not in any batch). ` +
        `${t.refused} row(s) will be kept and listed with a reason.\n\nContinue?`)) return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/cleanup-bill-payment-orphans", { dry_run: dryRun });
      if (dryRun) {
        setDupResult(null);
        setDupPreview(r.data);
        const t = dupTotals(r.data);
        toast.info(`Preview · ${t.deleted} duplicate row(s) can be removed, ${t.refused} refused (protected).`);
      } else {
        setDupPreview(null);
        setDupResult(r.data);
        const t = dupTotals(r.data);
        toast.success(`Done · ${t.deleted} duplicate row(s) removed. Bill Payment section is clean.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed (admin only)");
    } finally {
      setBusy(false);
    }
  }

  async function runSnap(dryRun) {
    if (!dryRun && !window.confirm(
      "Freeze beneficiary bank snapshots on all legacy Bill Payment Vouchers and Vendor Advances?\n\n" +
      "Resolution is strictly by Vendor ID / Code (never by name). Rows that cannot be " +
      "resolved are reported with a reason. Continue?")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/backfill-beneficiary-snapshots", { dry_run: dryRun });
      if (dryRun) { setSnapResult(null); setSnapPreview(r.data); }
      else        { setSnapPreview(null); setSnapResult(r.data); }
      const fixed = r.data.fixed || 0;
      const bad = (r.data.unresolved || []).length;
      (dryRun ? toast.info : toast.success)(
        `${dryRun ? "Preview" : "Done"} · ${fixed} snapshot(s) ${dryRun ? "can be" : ""} written · ${bad} row(s) need vendor-master fixes.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed (admin only)");
    } finally {
      setBusy(false);
    }
  }

  const dupActive = dupResult || dupPreview;
  const dupT = dupActive ? dupTotals(dupActive) : null;
  const snapActive = snapResult || snapPreview;

  return (
    <Card className="p-5" data-testid="payment-integrity-tile">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 bg-orange-50 border border-orange-100 rounded-full flex items-center justify-center">
          <ShieldAlert size={16} className="text-orange-700" />
        </div>
        <div>
          <h3 className="font-display text-base font-black text-slate-900">Payment Integrity</h3>
          <p className="text-[11px] text-slate-500">
            One-click repairs for the Bill Payment / Batch pipeline. Both actions are idempotent — safe to re-run.
          </p>
        </div>
      </div>

      {/* ── 1. Duplicate bill payment cleanup ─────────────────────── */}
      <div className="border border-slate-200 p-3 mb-3" data-testid="dup-cleanup-block">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Fix Duplicate Bill Payments</div>
        <p className="text-[11px] text-slate-500 mt-0.5 mb-2 leading-relaxed">
          Removes duplicate/orphan rows that make approved <strong>General / Salary / Store Rent</strong> payments
          or <strong>Purchase Invoices</strong> appear <strong>twice</strong> in the Bill Payment section.
          Rows already paid, batched, or carrying deductions/holds are never touched. If two separate
          Purchase Invoice <em>documents</em> duplicate the same real bill, they are reported below for manual review.
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => runDup(true)} disabled={busy} data-testid="dup-cleanup-preview-btn"
                  className="border border-slate-300 hover:border-slate-700 hover:bg-slate-50 text-slate-700 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Preview
          </button>
          <button onClick={() => runDup(false)} disabled={busy || !dupPreview} data-testid="dup-cleanup-run-btn"
                  className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Remove Duplicates
          </button>
        </div>
        {dupActive && dupT && (
          <div className={`mt-2 border px-3 py-2 text-xs ${dupResult ? "bg-emerald-50 border-emerald-300 text-emerald-900" : "bg-amber-50 border-amber-300 text-amber-900"}`}
               data-testid="dup-cleanup-report">
            <div className="font-bold">
              {dupResult ? "✓ Cleanup complete" : "Preview — nothing deleted yet"}
              {" · "}{dupT.deleted} duplicate row(s) {dupResult ? "removed" : "found"}
              {dupT.refused > 0 && <> · {dupT.refused} kept (protected)</>}
            </div>
            {(dupActive.rows || []).some(r => r.action === "duplicate_source_report") && (
              <div className="mt-1 text-[11px] font-bold text-rose-800" data-testid="dup-source-warning">
                ⚠ Duplicate Purchase Invoice document(s) detected — cancel the duplicate PI via the
                normal workflow, then re-run this cleanup:
                <ul className="mt-0.5 font-mono font-normal space-y-0.5 max-h-24 overflow-auto">
                  {(dupActive.rows || []).filter(r => r.action === "duplicate_source_report").slice(0, 20).map((r, i) => (
                    <li key={i}>{r.note}</li>
                  ))}
                </ul>
              </div>
            )}
            {(dupActive.rows || []).filter(r => r.action.startsWith("orphan") || r.action.startsWith("dupe")).length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer text-[11px] hover:underline">Show affected rows</summary>
                <ul className="mt-1 space-y-0.5 max-h-36 overflow-auto">
                  {(dupActive.rows || []).filter(r => r.action.startsWith("orphan") || r.action.startsWith("dupe")).slice(0, 100).map((r, i) => (
                    <li key={i} className="font-mono text-[10px]">
                      {r.source_voucher} — {r.action}{r.note ? ` (${r.note})` : ""}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {/* ── 2. Beneficiary snapshot backfill ──────────────────────── */}
      <div className="border border-slate-200 p-3" data-testid="snapshot-backfill-block">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Backfill Beneficiary Snapshots</div>
        <p className="text-[11px] text-slate-500 mt-0.5 mb-2 leading-relaxed">
          Freezes bank details onto legacy Bill Payment Vouchers / Vendor Advances so the batch
          <strong> bank Excel</strong> download unblocks. Same action as the <em>Auto-Fix Rows</em> button
          in the Verify Beneficiaries dialog, but across <strong>all batches at once</strong>.
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => runSnap(true)} disabled={busy} data-testid="snap-backfill-preview-btn"
                  className="border border-slate-300 hover:border-slate-700 hover:bg-slate-50 text-slate-700 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Preview
          </button>
          <button onClick={() => runSnap(false)} disabled={busy || !snapPreview} data-testid="snap-backfill-run-btn"
                  className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Run Backfill
          </button>
        </div>
        {snapActive && (
          <div className={`mt-2 border px-3 py-2 text-xs ${snapResult ? "bg-emerald-50 border-emerald-300 text-emerald-900" : "bg-amber-50 border-amber-300 text-amber-900"}`}
               data-testid="snap-backfill-report">
            <div className="font-bold">
              {snapResult ? "✓ Backfill complete" : "Preview — nothing written yet"}
              {" · "}{snapActive.fixed || 0} snapshot(s) {snapResult ? "written" : "writable"}
              {(snapActive.unresolved || []).length > 0 && <> · {(snapActive.unresolved || []).length} need vendor-master fixes</>}
            </div>
            {(snapActive.unresolved || []).length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer text-[11px] hover:underline">Show unresolved rows + reasons</summary>
                <ul className="mt-1 space-y-0.5 max-h-36 overflow-auto">
                  {(snapActive.unresolved || []).slice(0, 100).map((u, i) => (
                    <li key={i} className="font-mono text-[10px]">
                      {u.voucher_number} — {u.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function WipeDemoMastersTile() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);   // dry-run result
  const [result, setResult] = useState(null);     // committed result

  async function runDryRun() {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/wipe-demo-masters?dry_run=true", {});
      setPreview(r.data);
      toast.info(`Preview ready · ${r.data.totals.transactions_deleted} demo transactions + ${r.data.totals.masters_deleted} masters can be removed.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!preview) return;
    const summary = `${preview.totals.transactions_deleted} demo transactions and ${preview.totals.masters_deleted} demo masters will be permanently deleted.\n\n${preview.totals.masters_skipped} masters will be skipped (still in use by real transactions).\n\nThis cannot be undone. Continue?`;
    if (!window.confirm(summary)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/wipe-demo-masters", {});
      setResult(r.data);
      setPreview(null);
      toast.success(`Done · ${r.data.totals.transactions_deleted} transactions + ${r.data.totals.masters_deleted} masters wiped.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally {
      setBusy(false);
    }
  }

  const active = result || preview;

  return (
    <Card className="p-6" data-testid="wipe-demo-masters-tile">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 shrink-0">
          <Database size={22} />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-base font-black text-slate-900">Wipe Demo Masters</h3>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Removes the sample projects (RAD-001…005), clients (Delhi Metro, MCG, Apollo),
            vendors, items, members and assets that ship pre-seeded for evaluation. Only
            records that are <strong>not yet referenced by any real transaction</strong> are
            removed — anything in active use stays put with a &ldquo;skipped — in use by …&rdquo; note.
          </p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 px-3 py-2 mb-4 flex items-start gap-2">
        <ShieldAlert size={14} className="text-amber-700 mt-0.5 shrink-0" />
        <p className="text-[11px] text-amber-900 leading-relaxed">
          Library masters <strong>HSN / SAC and UOM</strong> are deliberately excluded — every tenant
          needs the GSTN-standard list. Run <em>Preview</em> first to see exactly what will change before committing.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={runDryRun} disabled={busy} data-testid="wipe-preview-btn"
                className="border border-slate-300 hover:border-slate-700 hover:bg-slate-50 text-slate-700 px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview
        </button>
        <button onClick={commit} disabled={busy || !preview} data-testid="wipe-commit-btn"
                className="bg-rose-600 hover:bg-rose-700 text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Confirm &amp; Wipe
        </button>
        {!preview && !result && (
          <span className="text-[10px] text-slate-400 ml-2">Click <strong>Preview</strong> to enable the wipe button.</span>
        )}
      </div>

      {active && <WipeReport report={active} committed={!!result} />}
    </Card>
  );
}

function WipeReport({ report, committed }) {
  const t = report.totals;
  return (
    <div className="mt-5 border border-slate-200 bg-slate-50/60 p-4" data-testid="wipe-report">
      <div className="flex items-center gap-2 mb-3">
        {committed ? <CheckCircle2 size={16} className="text-emerald-700" /> : <AlertTriangle size={16} className="text-amber-700" />}
        <h4 className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700">
          {committed ? "Wipe complete" : "Preview (no changes saved yet)"}
        </h4>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat label="Demo Transactions" value={t.transactions_deleted} tone="rose" />
        <Stat label="Masters Removed" value={t.masters_deleted} tone="rose" />
        <Stat label="Masters Skipped" value={t.masters_skipped} tone="amber" />
      </div>

      <div className="space-y-3">
        <Section title="Demo Transactions" data={report.transactions} kind="txn" />
        <Section title="Masters" data={report.masters} kind="master" />
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const tones = {
    rose:    "border-rose-200 bg-rose-50 text-rose-800",
    amber:   "border-amber-200 bg-amber-50 text-amber-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  };
  return (
    <div className={`border ${tones[tone] || tones.rose} px-3 py-2`}>
      <div className="text-[9px] uppercase tracking-[0.18em] font-bold opacity-70">{label}</div>
      <div className="text-xl font-black tabular mt-0.5">{value}</div>
    </div>
  );
}

function Section({ title, data, kind }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1.5">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
        {Object.entries(data).map(([coll, info]) => {
          const found = info.found ?? (info.ids?.length || info.deleted);
          if (!found && !info.deleted && (!info.skipped_in_use || info.skipped_in_use.length === 0)) return null;
          return (
            <div key={coll} className="text-[11px] flex items-start gap-2 bg-white border border-slate-200 px-3 py-2">
              <span className={`shrink-0 mt-0.5 w-1.5 h-1.5 rounded-full ${info.deleted > 0 ? "bg-rose-500" : (info.skipped_in_use && info.skipped_in_use.length > 0) ? "bg-amber-500" : "bg-slate-300"}`} />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-800 font-mono">{coll}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {kind === "master" && info.found !== undefined && <span>{info.found} found · </span>}
                  <span className="text-rose-700 font-bold">{info.deleted}</span> removed
                  {kind === "master" && info.skipped_in_use?.length > 0 && (
                    <> · <span className="text-amber-700 font-bold">{info.skipped_in_use.length}</span> skipped</>
                  )}
                </div>
                {info.skipped_in_use?.length > 0 && (
                  <details className="mt-1">
                    <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-800">Show skipped reasons</summary>
                    <ul className="mt-1 space-y-0.5">
                      {info.skipped_in_use.map((s, i) => (
                        <li key={i} className="text-[10px] text-slate-600 font-mono">
                          <span className="text-amber-700">{s.id}</span> — <span className="text-slate-500">{s.reason}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * BranchTagNormalizeTile — Sprint 4.10
 *
 * Surfaces and one-click fixes records that carry an orphan/ghost
 * `branch_id` so the operator's branch filter on Bill Payment, Bank Recon
 * and similar finance pages stops "hiding" data.
 *
 * Audit endpoint: GET /api/admin/branch-tag-audit
 * Normalize:      POST /api/admin/branch-tag-normalize
 */
function BranchTagNormalizeTile() {
  const [audit, setAudit] = useState(null);
  const [target, setTarget] = useState("");
  const [includeOrphan, setIncludeOrphan] = useState(true);
  const [includeGhost, setIncludeGhost] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function refresh() {
    try {
      const r = await api.get("/admin/branch-tag-audit");
      setAudit(r.data);
      if (!target && r.data.branches.length === 1) {
        setTarget(r.data.branches[0].id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    }
  }
  useEffect(() => { refresh(); }, []);

  // Total counts (used for the call-to-action gating)
  const totalOrphan = (audit?.audit || []).reduce((s, r) => s + (r.orphan || 0), 0);
  const totalGhost  = (audit?.audit || []).reduce((s, r) => s + (r.ghost || 0), 0);
  // Rows worth showing — only collections that actually have drift
  const rowsToShow  = (audit?.audit || []).filter(r => r.orphan > 0 || r.ghost > 0);

  async function commit() {
    if (!target) { toast.error("Pick a target branch first"); return; }
    if (totalOrphan + totalGhost === 0) { toast.info("Nothing to normalize."); return; }
    const targetName = audit.branches.find(b => b.id === target)?.branch_name || "?";
    const lines = [
      `Re-assign branch_id on ALL legacy / mis-tagged records to:`,
      `  → ${targetName}`,
      ``,
      includeOrphan ? `  • ${totalOrphan} orphan record(s)  (empty / null branch_id)` : "",
      includeGhost  ? `  • ${totalGhost} ghost record(s)   (branch_id points to a deleted branch)` : "",
      ``,
      `This is irreversible. Recommended for single-branch tenants.`,
      `Continue?`,
    ].filter(Boolean).join("\n");
    if (!window.confirm(lines)) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/branch-tag-normalize", {
        target_branch_id: target,
        include_orphan: includeOrphan,
        include_ghost:  includeGhost,
      });
      setResult(r.data);
      toast.success(`Re-tagged ${r.data.total_modified} record(s) to ${r.data.target_branch_name}`,
                     { duration: 7000 });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Normalize failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5" data-testid="branch-tag-normalize-tile">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 bg-blue-50 border border-blue-100 rounded-full flex items-center justify-center">
          <Building2 size={16} className="text-blue-700" />
        </div>
        <div>
          <h3 className="font-display text-base font-black text-slate-900">Normalize Branch Tags</h3>
          <p className="text-[11px] text-slate-500">Re-home legacy records whose <code className="font-mono text-[10px] bg-slate-100 px-1">branch_id</code> is missing or points to a deleted branch.</p>
        </div>
      </div>

      {!audit ? (
        <div className="text-xs text-slate-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Auditing tenant…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="border border-amber-200 bg-amber-50 px-3 py-2"
                 data-testid="branch-tag-orphan-count">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Orphan rows</div>
              <div className="font-display text-xl font-black text-amber-800 tabular-nums">{totalOrphan}</div>
              <div className="text-[10px] text-amber-700/80">empty / null branch_id</div>
            </div>
            <div className="border border-rose-200 bg-rose-50 px-3 py-2"
                 data-testid="branch-tag-ghost-count">
              <div className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">Ghost rows</div>
              <div className="font-display text-xl font-black text-rose-800 tabular-nums">{totalGhost}</div>
              <div className="text-[10px] text-rose-700/80">points to a deleted branch</div>
            </div>
          </div>

          <div className="space-y-2 mb-3 text-xs">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Re-home onto branch *</span>
              <select data-testid="branch-tag-target"
                      value={target} onChange={e => setTarget(e.target.value)}
                      className="w-full border border-slate-300 px-3 py-2 text-sm">
                <option value="">— Select target branch —</option>
                {audit.branches.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.branch_name || "(unnamed)"} {audit.branches.length === 1 ? " · only branch" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="branch-tag-incl-orphan"
              checked={includeOrphan} onChange={e => setIncludeOrphan(e.target.checked)} />
              Include orphan rows ({totalOrphan})</label>
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="branch-tag-incl-ghost"
              checked={includeGhost} onChange={e => setIncludeGhost(e.target.checked)} />
              Include ghost rows ({totalGhost})</label>
          </div>

          {rowsToShow.length > 0 && (
            <details className="text-xs mb-3" data-testid="branch-tag-details">
              <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
                Per-collection breakdown ({rowsToShow.length} collections with drift)
              </summary>
              <table className="w-full mt-2 text-[11px] border border-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-bold text-slate-600">Collection</th>
                    <th className="text-right px-2 py-1.5 font-bold text-slate-600">Total</th>
                    <th className="text-right px-2 py-1.5 font-bold text-amber-700">Orphan</th>
                    <th className="text-right px-2 py-1.5 font-bold text-rose-700">Ghost</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsToShow.map(r => (
                    <tr key={r.collection} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-mono">{r.collection}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{r.total}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-amber-700">{r.orphan || ""}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-rose-700">{r.ghost || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          <button data-testid="branch-tag-normalize-btn"
                  disabled={busy || !target || (totalOrphan + totalGhost === 0)}
                  onClick={commit}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {busy ? "Normalizing…" : "Normalize Branch Tags"}
          </button>

          {result && (
            <div className="mt-3 bg-emerald-50 border border-emerald-300 px-3 py-2 text-xs"
                 data-testid="branch-tag-result">
              <div className="font-bold text-emerald-900">
                ✓ {result.total_modified} record(s) re-tagged to {result.target_branch_name}
              </div>
              <div className="text-emerald-700 text-[11px] mt-1">
                The branch filter on Bill Payment, Bank Reconciliation and the other finance modules will now show these records.
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────────
// Sprint 4.11.2 — Issue Voucher "From Warehouse" backfill tile
// Bulk-created vouchers from pre-fix builds carry the warehouse on
// every line but the IV header's `from_warehouse_*` trio is blank,
// so the IV list shows "—" in the "From Warehouse" column. This
// 1-click tool copies the warehouse from line[0] into the header
// for every affected voucher. Idempotent — safe to run multiple
// times.
// ─────────────────────────────────────────────────────────────────
function IvFromWarehouseBackfillTile() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    if (!window.confirm(
      "This will scan every Issue Voucher and patch:\n" +
      "  • Header 'From Warehouse' (copied from line[0] when blank)\n" +
      "  • Header 'Ship-To Label' (looked up from the client's shipping addresses by city/state/pincode)\n\n" +
      "Both patches are idempotent and safe to re-run. Continue?",
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/issue-vouchers-bulk/backfill-from-warehouse", {});
      setResult(r.data);
      toast.success(`Patched ${r.data.patched} issue voucher(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5" data-testid="iv-from-warehouse-backfill-tile">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 bg-indigo-50 border border-indigo-100 rounded-full flex items-center justify-center">
          <PackageMinus size={16} className="text-indigo-700" />
        </div>
        <div>
          <h3 className="font-display text-base font-black text-slate-900">Backfill Issue-Voucher Header (Warehouse + Ship-To Label)</h3>
          <p className="text-[11px] text-slate-500">
            For older bulk-uploaded IVs whose header <code className="font-mono text-[10px] bg-slate-100 px-1">from_warehouse_id</code> is blank
            (list shows &ldquo;— no warehouse&rdquo;) OR whose <code className="font-mono text-[10px] bg-slate-100 px-1">ship_to_label</code> is blank
            (list shows &ldquo;NA&rdquo; / blank). Warehouse is copied from line[0]; Ship-To Label is reverse-matched from the client&rsquo;s shipping_addresses.
          </p>
        </div>
      </div>

      <button data-testid="iv-from-warehouse-backfill-btn"
              disabled={busy}
              onClick={run}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-2">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
        {busy ? "Backfilling…" : "Run Backfill"}
      </button>

      {result && (
        <div className="mt-3 bg-emerald-50 border border-emerald-300 px-3 py-2 text-xs"
             data-testid="iv-from-warehouse-backfill-result">
          <div className="font-bold text-emerald-900">
            ✓ {result.patched} field patch(es) applied
          </div>
          <div className="mt-1 text-[11px] text-emerald-800 space-y-0.5">
            <div>• <span className="font-bold tabular-nums">{result.patched_warehouse ?? 0}</span> voucher(s) had their <span className="font-mono">from_warehouse</span> trio set</div>
            <div>• <span className="font-bold tabular-nums">{result.patched_ship_to_label ?? 0}</span> voucher(s) had their <span className="font-mono">ship_to_label</span> filled</div>
          </div>
          {result.patched > 0 && result.voucher_numbers?.length > 0 && (
            <details className="mt-1.5">
              <summary className="cursor-pointer text-emerald-700 hover:text-emerald-900 text-[11px]">
                Show affected voucher numbers
              </summary>
              <div className="mt-1 text-[11px] text-emerald-700 font-mono leading-relaxed max-h-32 overflow-y-auto">
                {result.voucher_numbers.join(", ")}
              </div>
            </details>
          )}
          {result.patched === 0 && (
            <div className="text-emerald-700 text-[11px] mt-1">
              No vouchers needed patching — all IV headers already have a warehouse + ship-to label set.
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────────
// 2026-Feb — Backfill Vendor-Bill Line project_id from project_name
//
// Legacy Approved Salary / Store Rent / General Payment vouchers have
// lines carrying free-text `project_name` (e.g. "MMG-BED-Jun26") with
// an empty `project_id`. The aggregator matches on project_id → those
// amounts silently vanish from every Project P&L and the Home →
// Project-Cost-Control strip. This tile drives the admin repair endpoint
// `POST /api/admin/backfill-vendor-bill-project-ids` end-to-end:
//   1. Dry-run — Matched vs Unresolved counts (no data touched).
//   2. Show the top unresolved rows so the operator can create the
//      missing project master or fix the voucher label.
//   3. Apply — stamps project_id on the matched lines, writes an
//      audit_logs entry.
// Idempotent — safe to re-run.
// ─────────────────────────────────────────────────────────────────
function BackfillVBProjectIdTile() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);    // dry_run response
  const [result, setResult] = useState(null);      // apply response
  const [modules, setModules] = useState({
    salary_payment:  true,
    store_rent:      true,
    general_payment: true,
  });

  function activeModules() {
    return Object.entries(modules).filter(([, v]) => v).map(([k]) => k);
  }

  async function runDry() {
    const mods = activeModules();
    if (mods.length === 0) { toast.error("Select at least one module"); return; }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/backfill-vendor-bill-project-ids", {
        dry_run: true,
        modules: mods,
      });
      setPreview(r.data);
      const t = r.data.totals || {};
      toast.info(`Preview · ${t.matched_lines || 0} line(s) matched, ${t.unresolved_lines || 0} unresolved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (!preview) return;
    const t = preview.totals || {};
    const matched = t.matched_lines || 0;
    const unresolved = t.unresolved_lines || 0;
    if (matched === 0) { toast.info("Nothing to apply — no matches found."); return; }
    const summary = [
      `Stamp project_id on ${matched} matched line(s) across ${(preview.modules && Object.keys(preview.modules).length) || 0} module(s).`,
      unresolved > 0 ? `${unresolved} line(s) will remain unresolved — you can create the missing project master OR edit the voucher label and re-run.` : "",
      ``,
      `The change is audit-trailed but not reversible via this tool. Continue?`,
    ].filter(Boolean).join("\n");
    if (!window.confirm(summary)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/backfill-vendor-bill-project-ids", {
        dry_run: false,
        modules: activeModules(),
      });
      setResult(r.data);
      setPreview(null);
      toast.success(`Done · ${r.data.totals.matched_lines} line(s) stamped, ${r.data.totals.vouchers_touched || (r.data.modules && Object.values(r.data.modules).reduce((s, m) => s + (m.vouchers_touched || 0), 0)) || 0} voucher(s) touched.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setBusy(false);
    }
  }

  const active = result || preview;
  const t = active?.totals || {};
  const modLabels = { salary_payment: "Salary Payment", store_rent: "Store Rent Payment", general_payment: "General Payment" };

  return (
    <Card className="p-5" data-testid="backfill-vb-project-id-tile">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 bg-emerald-50 border border-emerald-100 rounded-full flex items-center justify-center shrink-0">
          <FolderTree size={16} className="text-emerald-700" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-base font-black text-slate-900">Backfill Vendor-Bill Project IDs</h3>
          <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
            Repairs legacy Approved Salary / Store Rent / General Payment lines whose <code className="font-mono text-[10px] bg-slate-100 px-1">project_id</code> is blank but the <code className="font-mono text-[10px] bg-slate-100 px-1">project_name</code> column holds a real label (e.g. <em>MMG-BED-Jun26</em>). Such lines never reach Project P&amp;L or the Home → Project-Cost-Control strip. Resolution: case-insensitive match on <em>project_name</em> first, then <em>project_code</em>.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        {Object.keys(modules).map(m => (
          <label key={m} className="text-[11px] flex items-center gap-1.5" data-testid={`backfill-vb-mod-${m}`}>
            <input type="checkbox" checked={modules[m]}
                   onChange={e => setModules(prev => ({ ...prev, [m]: e.target.checked }))} />
            <span className="text-slate-700">{modLabels[m]}</span>
          </label>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <button onClick={runDry} disabled={busy}
                data-testid="backfill-vb-dry-run-btn"
                className="border border-slate-300 hover:border-slate-700 hover:bg-slate-50 text-slate-700 px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
          {busy && !result ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
          Dry Run
        </button>
        <button onClick={apply} disabled={busy || !preview || (preview?.totals?.matched_lines || 0) === 0}
                data-testid="backfill-vb-apply-btn"
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
          {busy && preview ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          Apply
        </button>
        {!preview && !result && (
          <span className="text-[10px] text-slate-400 ml-2">Run <strong>Dry Run</strong> first to enable Apply.</span>
        )}
      </div>

      {active && (
        <div className="mt-4 border border-slate-200 bg-slate-50/60 p-4" data-testid="backfill-vb-report">
          <div className="flex items-center gap-2 mb-3">
            {result ? <CheckCircle2 size={16} className="text-emerald-700" /> : <AlertTriangle size={16} className="text-amber-700" />}
            <h4 className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700">
              {result ? "Backfill complete" : "Dry Run — no changes saved yet"}
            </h4>
          </div>

          <div className="grid grid-cols-4 gap-2 mb-3">
            <Stat label="Orphan Lines" value={t.orphan_lines || 0} tone="amber" />
            <Stat label="Matched"      value={t.matched_lines || 0} tone="emerald" />
            <Stat label="Unresolved"   value={t.unresolved_lines || 0} tone="rose" />
            <Stat label={result ? "Vouchers Updated" : "Would Update"} value={result ? (t.modified_lines || 0) : (t.matched_lines || 0)} tone={result ? "emerald" : "amber"} />
          </div>

          {/* Per-module breakdown */}
          {active.modules && Object.keys(active.modules).length > 0 && (
            <details className="text-xs mb-3" data-testid="backfill-vb-per-module">
              <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
                Per-module breakdown
              </summary>
              <table className="w-full mt-2 text-[11px] border border-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-bold text-slate-600">Module</th>
                    <th className="text-right px-2 py-1.5 font-bold text-slate-600">Scanned</th>
                    <th className="text-right px-2 py-1.5 font-bold text-amber-700">Orphan</th>
                    <th className="text-right px-2 py-1.5 font-bold text-emerald-700">Matched</th>
                    <th className="text-right px-2 py-1.5 font-bold text-rose-700">Unresolved</th>
                    <th className="text-right px-2 py-1.5 font-bold text-slate-600">Vouchers</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(active.modules).map(([mod, stats]) => (
                    <tr key={mod} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-mono">{modLabels[mod] || mod}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{stats.scanned_lines}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-amber-700">{stats.orphan_lines || ""}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-emerald-700">{stats.matched_lines || ""}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-rose-700">{stats.unresolved_lines || ""}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{stats.vouchers_touched || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          {/* Unresolved sample — most actionable data */}
          {active.unresolved_sample && active.unresolved_sample.length > 0 && (
            <div className="mt-2" data-testid="backfill-vb-unresolved">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-rose-700 mb-1.5">
                Unresolved lines ({(t.unresolved_lines || 0)} total, showing top {active.unresolved_sample.length})
              </div>
              <div className="text-[10px] text-slate-500 mb-1.5">
                These project names don&rsquo;t match any project master row. Either <strong>create the project</strong> in Masters → Projects, or edit the voucher line&rsquo;s project label, then re-run.
              </div>
              <table className="w-full text-[11px] border border-slate-200">
                <thead className="bg-rose-50">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-bold text-rose-700">Module</th>
                    <th className="text-left px-2 py-1.5 font-bold text-rose-700">Voucher</th>
                    <th className="text-left px-2 py-1.5 font-bold text-rose-700">Line</th>
                    <th className="text-left px-2 py-1.5 font-bold text-rose-700">Project Name (unmatched)</th>
                    <th className="text-left px-2 py-1.5 font-bold text-rose-700">Vendor</th>
                    <th className="text-right px-2 py-1.5 font-bold text-rose-700">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {active.unresolved_sample.map((row, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-mono text-[10px]">{modLabels[row.module] || row.module}</td>
                      <td className="px-2 py-1 font-mono">{row.voucher_number}</td>
                      <td className="px-2 py-1 tabular-nums">L{row.line_index}</td>
                      <td className="px-2 py-1 font-mono text-rose-800">{row.project_name}</td>
                      <td className="px-2 py-1 text-slate-700">{row.vendor_name}</td>
                      <td className="px-2 py-1 text-right tabular-nums">₹{(row.amount || 0).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result && (t.matched_lines || 0) > 0 && (
            <div className="mt-3 bg-emerald-50 border border-emerald-300 px-3 py-2 text-xs">
              <div className="font-bold text-emerald-900">
                ✓ {t.matched_lines} line(s) stamped with project_id.
              </div>
              <div className="text-emerald-700 text-[11px] mt-1">
                Refresh the Home → Project Health strip or Reports → Project P&amp;L — the newly-linked amounts will now appear in each project&rsquo;s <em>Consumed</em> / <em>Salary</em> column. An audit trail was written to <code className="font-mono text-[10px] bg-emerald-100 px-1">audit_logs</code>.
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────
// Sprint 2026-Feb — SI Warehouse Repair (Stock-Out Phase 2)
//
// Identifies Sales Invoices that carry at least one Inventory line
// item but have no `warehouse_id` assigned. Groups counts per branch
// so the admin can bulk-assign a warehouse in one click. Once every
// legacy Inventory SI has a warehouse, the strict Phase 2 validator
// stops blocking new approvals — and the SI Stock-Out feature flag
// can be safely flipped ON with per-warehouse accuracy intact.
// ────────────────────────────────────────────────────────────────
function SiWarehouseRepairTile() {
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [warehouses, setWarehouses] = useState({});     // { branch_id: [wh,...] }
  const [selection, setSelection] = useState({});       // { branch_id: warehouse_id }
  const [assignResult, setAssignResult] = useState(null);

  async function refresh() {
    setBusy(true);
    setAssignResult(null);
    try {
      const r = await api.get("/admin/si-warehouse-repair/summary");
      setSummary(r.data);
      // For each branch surfaced, prefetch its warehouses (small
      // number of network calls; branches with orphans usually ≤ 3).
      const nextWhs = {};
      for (const row of r.data.per_branch || []) {
        if (!row.branch_id) continue;
        try {
          const w = await api.get("/warehouses/picker", { params: { branch_id: row.branch_id } });
          nextWhs[row.branch_id] = w.data || [];
        } catch { /* ignore per-branch fetch errors */ }
      }
      setWarehouses(nextWhs);
      toast.info(`Scan complete · ${r.data.total_orphans} SI(s) missing warehouse across ${r.data.per_branch.length} branch(es).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setBusy(false);
    }
  }

  async function assign(branch_id) {
    const warehouse_id = selection[branch_id];
    if (!warehouse_id) {
      toast.error("Pick a warehouse for this branch first.");
      return;
    }
    const branchRow = (summary?.per_branch || []).find(r => r.branch_id === branch_id);
    const label = branchRow?.branch_name || branch_id;
    if (!window.confirm(
      `Assign the selected warehouse to ${branchRow?.count || 0} legacy SI(s) in branch "${label}"?\n\n`
      + `Approved SIs will keep their existing status. This just backfills the missing warehouse_id. Idempotent — safe to repeat.`
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/si-warehouse-repair/assign", {
        branch_id, warehouse_id, dry_run: false,
      });
      setAssignResult({ branch_id, ...r.data });
      toast.success(`Done · ${r.data.modified} SI(s) updated for ${label}.`);
      // Refresh scan so the count shrinks.
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card data-testid="si-warehouse-repair-tile">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-teal-50 rounded-lg text-teal-700">
          <Warehouse className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-slate-900 mb-1">Backfill SI Warehouse IDs</h3>
          <p className="text-sm text-slate-600 mb-4">
            Repairs legacy Sales Invoices that carry Inventory items but have no <code className="font-mono text-xs bg-slate-100 px-1">warehouse_id</code>.
            Required before Sales-Invoice Stock-Out can run with per-warehouse accuracy.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="si-wh-repair-scan"
              onClick={refresh}
              disabled={busy}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-800 text-white text-sm hover:bg-slate-900 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              Scan Legacy SIs
            </button>
          </div>

          {summary && (
            <div className="mt-4 space-y-3">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
                <div><strong>{summary.total_orphans}</strong> Sales Invoice(s) with Inventory lines are missing a warehouse.</div>
                {summary.total_orphans === 0 && (
                  <div className="text-emerald-700 mt-1 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> All SIs are clean — Phase 2 mandate is satisfied.
                  </div>
                )}
              </div>

              {(summary.per_branch || []).map(row => (
                <div key={row.branch_id || "__no_branch__"} className="rounded-md border border-slate-200 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div>
                      <div className="font-medium text-slate-800">{row.branch_name || "(no branch)"}</div>
                      <div className="text-xs text-slate-500">
                        {row.count} SI(s) · {row.approved_count} already Approved
                      </div>
                    </div>
                  </div>
                  {row.branch_id && (
                    <div className="flex flex-wrap gap-2 items-center">
                      <select
                        data-testid={`si-wh-repair-select-${row.branch_id}`}
                        value={selection[row.branch_id] || ""}
                        onChange={e => setSelection(s => ({ ...s, [row.branch_id]: e.target.value }))}
                        className="text-sm border border-slate-300 rounded px-2 py-1 bg-white"
                      >
                        <option value="">— Pick a warehouse —</option>
                        {(warehouses[row.branch_id] || []).map(w => (
                          <option key={w.id} value={w.id}>
                            {w.warehouse_code} — {w.warehouse_name}
                            {w.is_default ? " · Default" : ""}
                          </option>
                        ))}
                      </select>
                      <button
                        data-testid={`si-wh-repair-assign-${row.branch_id}`}
                        disabled={busy || !selection[row.branch_id]}
                        onClick={() => assign(row.branch_id)}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-50"
                      >
                        <Play className="w-3.5 h-3.5" /> Assign
                      </button>
                    </div>
                  )}
                  {!row.branch_id && (
                    <div className="text-xs text-amber-700 inline-flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      These SIs are missing branch_id — edit each one manually to set branch first.
                    </div>
                  )}
                </div>
              ))}

              {summary.sample?.length > 0 && (
                <details className="rounded-md bg-slate-50 border border-slate-200 p-3">
                  <summary className="cursor-pointer text-sm text-slate-700 font-medium">
                    Sample orphan SIs ({summary.sample.length})
                  </summary>
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-slate-500">
                        <tr>
                          <th className="text-left px-2 py-1">Invoice #</th>
                          <th className="text-left px-2 py-1">Date</th>
                          <th className="text-left px-2 py-1">Branch</th>
                          <th className="text-left px-2 py-1">Client</th>
                          <th className="text-left px-2 py-1">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.sample.map(s => (
                          <tr key={s.id} className="border-t border-slate-200">
                            <td className="px-2 py-1 font-mono">{s.invoice_number}</td>
                            <td className="px-2 py-1">{s.invoice_date}</td>
                            <td className="px-2 py-1">{s.branch_name}</td>
                            <td className="px-2 py-1">{s.client_name}</td>
                            <td className="px-2 py-1">{s.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </div>
          )}

          {assignResult && (
            <div className="mt-3 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
              <div className="inline-flex items-center gap-1 font-medium">
                <CheckCircle2 className="w-4 h-4" /> Assigned
              </div>
              <div>{assignResult.modified} SI(s) updated · Warehouse: <strong>{assignResult.warehouse_name}</strong></div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────────
// Sprint 2026-Feb-10 — Stock Integrity Audit tile
// Surfaces the read-only `/admin/stock-integrity-audit` endpoint.
// Flags 6 categories of ghost / missing / anomalous inventory data:
//   1. Negative-qty batches
//   2. Ledger vs batch mismatch
//   3. Over-committed items (pending IV+SI > physical)
//   4. Orphaned IV batch references
//   5. Suspicious batch touches (qty drift w/ no ledger)
//   6. Ghost-item batches (batch → deleted/inactive item — the
//      root cause of "item shows in stock but missing from
//      Adjustment / Issue picker" reports)
// ─────────────────────────────────────────────────────────────────
const FINDING_META = {
  ghost_item_batches:    { title: "Ghost-item batches",    hint: "Stock rows whose item was deleted / de-activated — invisible in voucher pickers.", tone: "rose" },
  negative_batches:      { title: "Negative-qty batches",  hint: "remaining_qty < 0 — impossible in the physical world.",                             tone: "rose" },
  ledger_batch_mismatch: { title: "Ledger vs batch drift", hint: "Σ(stock_ledger) ≠ (received − remaining) for the batch.",                          tone: "amber" },
  over_committed:        { title: "Over-committed items",  hint: "Pending IV+SI reservations exceed physical stock in the warehouse.",              tone: "amber" },
  orphaned_iv_batches:   { title: "Orphaned IV batches",   hint: "Approved IV line points to a batch that no longer exists.",                       tone: "amber" },
  suspicious_touches:    { title: "Suspicious untouched",  hint: "Batch qty differs from received but NO ledger row exists — silent drift.",        tone: "amber" },
};

function StockIntegrityAuditTile() {
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState(null);
  const [openKey, setOpenKey] = useState(null);   // which category's rows are expanded

  async function run() {
    setBusy(true);
    setAudit(null);
    setOpenKey(null);
    try {
      const r = await api.get("/admin/stock-integrity-audit");
      setAudit(r.data);
      const anyFound = r.data?.any_issues_found;
      if (anyFound) toast.warning("Anomalies detected — review categories below.");
      else toast.success("Clean ✓ No ghost / missing data found.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed (admin role required)");
    } finally { setBusy(false); }
  }

  async function downloadXlsx() {
    const t = toast.loading("Preparing Excel…");
    try {
      const r = await api.get("/admin/stock-integrity-audit.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `stock_integrity_audit_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded", { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed", { id: t });
    }
  }

  const summary = audit?.summary || {};
  const findings = audit?.findings || {};
  const totalIssues = Object.values(summary).reduce((s, n) => s + Number(n || 0), 0);

  return (
    <Card className="p-5" data-testid="stock-integrity-audit-tile">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 bg-teal-50 border border-teal-100 rounded-full flex items-center justify-center">
          <ShieldAlert size={16} className="text-teal-700" />
        </div>
        <div>
          <h3 className="font-display text-base font-black text-slate-900">Stock Integrity Audit</h3>
          <p className="text-[11px] text-slate-500">Read-only. Detects ghost / missing / anomalous rows across <code className="font-mono text-[10px] bg-slate-100 px-1">inventory_batches</code>, <code className="font-mono text-[10px] bg-slate-100 px-1">stock_ledger</code> and pending vouchers.</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <button data-testid="stock-audit-run-btn"
                onClick={run} disabled={busy}
                className="bg-teal-700 hover:bg-teal-800 disabled:opacity-50 text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
          {busy ? "Scanning…" : "Run Audit"}
        </button>
        <button data-testid="stock-audit-xlsx-btn"
                onClick={downloadXlsx} disabled={busy || !audit}
                className="bg-slate-100 hover:bg-slate-200 disabled:opacity-40 text-slate-700 border border-slate-300 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
          <Download size={12} /> Excel
        </button>
      </div>

      {!audit ? (
        <div className="text-[11px] text-slate-400 border border-dashed border-slate-200 px-3 py-4 text-center">
          Click <span className="font-bold">Run Audit</span> to scan the live database. No writes are made.
        </div>
      ) : (
        <>
          <div className={`border px-3 py-2 mb-3 text-xs ${totalIssues === 0 ? "bg-emerald-50 border-emerald-300 text-emerald-900" : "bg-amber-50 border-amber-300 text-amber-900"}`}
               data-testid="stock-audit-result-banner">
            {totalIssues === 0
              ? <><CheckCircle2 size={12} className="inline-block mr-1" /><strong>Clean — no anomalies detected.</strong></>
              : <><AlertTriangle size={12} className="inline-block mr-1" /><strong>{totalIssues}</strong> anomal{totalIssues === 1 ? "y" : "ies"} across <strong>{Object.values(summary).filter(n => n > 0).length}</strong> categor{Object.values(summary).filter(n => n > 0).length === 1 ? "y" : "ies"}. Read-only — repairs require an explicit admin script.</>
            }
          </div>

          <div className="space-y-1.5">
            {Object.entries(FINDING_META).map(([key, meta]) => {
              const count = Number(summary[key] || 0);
              const rows = findings[key] || [];
              const isOpen = openKey === key;
              const toneCls = count === 0
                ? "bg-slate-50 border-slate-200 text-slate-500"
                : (meta.tone === "rose"
                    ? "bg-rose-50 border-rose-200 text-rose-900"
                    : "bg-amber-50 border-amber-200 text-amber-900");
              return (
                <div key={key} className={`border ${toneCls}`} data-testid={`stock-audit-cat-${key}`}>
                  <button
                    disabled={count === 0}
                    onClick={() => setOpenKey(isOpen ? null : key)}
                    className="w-full px-3 py-2 flex items-center justify-between text-left disabled:cursor-default">
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider">{meta.title}</div>
                      <div className="text-[10px] opacity-80">{meta.hint}</div>
                    </div>
                    <div className="tabular-nums font-black text-lg" data-testid={`stock-audit-count-${key}`}>{count}</div>
                  </button>
                  {isOpen && count > 0 && (
                    <div className="border-t border-current/20 max-h-64 overflow-auto bg-white/60"
                         data-testid={`stock-audit-rows-${key}`}>
                      <table className="w-full text-[10px]">
                        <thead className="bg-white/80 sticky top-0">
                          <tr>
                            {Object.keys(rows[0] || {}).map(h => (
                              <th key={h} className="text-left px-2 py-1 font-bold text-slate-600 uppercase tracking-wider">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              {Object.keys(rows[0]).map(h => (
                                <td key={h} className="px-2 py-1 font-mono tabular-nums text-slate-700 whitespace-nowrap">
                                  {typeof r[h] === "number" ? Number(r[h]).toFixed(2) : (r[h] ?? "—")}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
}


// ═════════════════════════════════════════════════════════════════════
// Sprint 2026-Feb-10h · Project Access Audit + Bulk Assign
// ═════════════════════════════════════════════════════════════════════
// Combines:
//   1. Strict-mode toggle (`system_settings.access_control.strict_project_access`)
//   2. Read-only audit — projects with/without an `authorized_users` list
//   3. Bulk assign — pick projects (from the "empty" list or all Active),
//      pick users, choose Append/Replace, hit Apply
//   4. Excel download of the audit
// ═════════════════════════════════════════════════════════════════════
function ProjectAccessAuditTile() {
  const [strictMode, setStrictMode] = useState(null);   // null = not-yet-loaded
  const [savingStrict, setSavingStrict] = useState(false);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [selectedUserIds,    setSelectedUserIds]    = useState([]);
  const [mode, setMode] = useState("append");
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [filterEmpty, setFilterEmpty] = useState(true);   // show only projects missing authorized_users
  const [projSearch, setProjSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    api.get("/settings/access-control")
      .then(r => setStrictMode(!!r.data?.strict_project_access))
      .catch(() => setStrictMode(false));
    api.get("/users")
      .then(r => setUsers((r.data || []).filter(u => u.is_active !== false)))
      .catch(() => {});
  }, []);

  async function toggleStrictMode(next) {
    setSavingStrict(true);
    try {
      await api.put("/settings/access-control", { strict_project_access: next });
      setStrictMode(next);
      toast.success(next
        ? "Strict mode ON — restricted users now only see projects with them explicitly assigned."
        : "Strict mode OFF — grandfather clause restored.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed (admin only)");
    } finally { setSavingStrict(false); }
  }

  async function runAudit() {
    setBusy(true);
    setApplyResult(null);
    try {
      const r = await api.get("/admin/project-access-audit");
      setAudit(r.data);
      setSelectedProjectIds([]);
      const empty = r.data?.summary?.without_users ?? 0;
      if (empty > 0) toast.warning(`${empty} project(s) missing authorized_users list.`);
      else           toast.success("All active projects have an authorized_users list ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed (admin only)");
    } finally { setBusy(false); }
  }

  async function downloadXlsx() {
    const t = toast.loading("Preparing Excel…");
    try {
      const r = await api.get("/admin/project-access-audit.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `project_access_audit_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded", { id: t });
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed", { id: t }); }
  }

  // Sprint 2026-Feb-10j — Excel bulk template + upload
  async function downloadBulkTemplate() {
    const t = toast.loading("Preparing template…");
    try {
      const r = await api.get("/admin/project-access-bulk-template.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `project_access_bulk_template_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded — edit AMBER columns and re-upload.", { id: t });
    } catch (e) { toast.error(e?.response?.data?.detail || "Template download failed", { id: t }); }
  }
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading,    setUploading]    = useState(false);
  async function handleBulkUpload(evt) {
    const file = evt.target.files?.[0];
    // Reset the input immediately so re-uploading the same file works.
    evt.target.value = "";
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/project-access-bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(r.data);
      const { projects_renamed = 0, projects_reassigned = 0, rows_processed = 0, warnings = [] } = r.data;
      if (rows_processed === 0) {
        // Nothing actually changed — most common cause: the admin edited the
        // grey "Project Name (current)" column instead of the amber
        // "New Project Name" (col C). Be honest instead of toasting success.
        toast.warning(
          "Upload processed but NO changes were applied. Make sure you fill the "
          + "AMBER columns — 'New Project Name' (col C) to rename, "
          + "'New Authorized Users (Emails)' (col G) to reassign. "
          + "Edits to the grey 'current' columns are ignored."
          + (warnings.length > 0 ? ` · ${warnings.length} warning(s) below.` : ""),
          { duration: 10000 }
        );
      } else {
        toast.success(
          `${rows_processed} project(s) updated · renamed ${projects_renamed} · reassigned ${projects_reassigned}`
          + (warnings.length > 0 ? ` · ${warnings.length} warning(s)` : "")
        );
        // Renames must propagate to any already-open Projects tab (tabs stay
        // mounted when hidden) + voucher-form project pickers.
        triggerMastersRefresh();
      }
      await runAudit();  // refresh list
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  }

  async function applyBulk() {
    if (selectedProjectIds.length === 0 || selectedUserIds.length === 0) {
      toast.error("Select at least one project and one user first.");
      return;
    }
    if (mode === "replace" && !window.confirm(
      `REPLACE mode will OVERWRITE the authorized_users list on ${selectedProjectIds.length} project(s) — any existing users NOT in your selection will be removed. Continue?`
    )) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const r = await api.post("/admin/project-access-bulk-assign", {
        project_ids: selectedProjectIds,
        user_ids:    selectedUserIds,
        mode,
      });
      setApplyResult(r.data);
      toast.success(`Updated ${r.data.modified} project(s). ${r.data.unchanged > 0 ? `(${r.data.unchanged} already matched)` : ""}`);
      await runAudit();  // refresh
      setSelectedProjectIds([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk apply failed");
    } finally { setApplying(false); }
  }

  const visibleProjects = (audit
    ? (filterEmpty ? audit.without_users : [...audit.without_users, ...audit.with_users])
    : []
  ).filter(p => {
    if (!projSearch.trim()) return true;
    const q = projSearch.trim().toLowerCase();
    return (p.project_code || "").toLowerCase().includes(q)
        || (p.project_name || "").toLowerCase().includes(q)
        || (p.client_name  || "").toLowerCase().includes(q)
        || (p.branch_name  || "").toLowerCase().includes(q);
  });
  const visibleUsers = users.filter(u => {
    if (!userSearch.trim()) return true;
    const q = userSearch.trim().toLowerCase();
    return (u.name || "").toLowerCase().includes(q)
        || (u.email|| "").toLowerCase().includes(q)
        || (u.role || "").toLowerCase().includes(q);
  });

  function toggleProjSelect(pid) {
    setSelectedProjectIds(prev => prev.includes(pid)
      ? prev.filter(x => x !== pid) : [...prev, pid]);
  }
  function toggleUserSelect(uid) {
    setSelectedUserIds(prev => prev.includes(uid)
      ? prev.filter(x => x !== uid) : [...prev, uid]);
  }
  function selectAllVisibleProjects() {
    setSelectedProjectIds(Array.from(new Set([
      ...selectedProjectIds, ...visibleProjects.map(p => p.id),
    ])));
  }

  return (
    <Card className="p-5 lg:col-span-2" data-testid="project-access-audit-tile">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 bg-indigo-50 border border-indigo-100 rounded-full flex items-center justify-center">
          <Users size={16} className="text-indigo-700" />
        </div>
        <div>
          <h3 className="font-display text-base font-black text-slate-900">Project Access Audit &amp; Bulk Assign</h3>
          <p className="text-[11px] text-slate-500">Identify projects missing an <code className="font-mono text-[10px] bg-slate-100 px-1">authorized_users</code> list and bulk-populate them BEFORE flipping strict-mode ON.</p>
        </div>
      </div>

      {/* ── Strict-mode toggle ─────────────────────────────────── */}
      <div className={`border p-2.5 mb-3 ${strictMode ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`}
           data-testid="strict-mode-block">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-0.5">
              Strict Project Access Mode
            </div>
            <div className="text-[11px] text-slate-600">
              {strictMode
                ? <span><strong className="text-emerald-800">ON</strong> — restricted users can ONLY see projects that explicitly list them in <code className="font-mono text-[10px] bg-white px-1">authorized_users</code>. Projects with empty lists are admin-only.</span>
                : <span><strong className="text-amber-800">OFF</strong> — grandfather clause active. Restricted users see projects with empty <code className="font-mono text-[10px] bg-white px-1">authorized_users</code> as long as branch matches. Flip ON only after populating lists.</span>
              }
            </div>
          </div>
          <button
            data-testid="strict-mode-toggle-btn"
            disabled={savingStrict || strictMode === null}
            onClick={() => toggleStrictMode(!strictMode)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider font-bold border ${
              strictMode
                ? "bg-emerald-700 hover:bg-emerald-800 text-white border-emerald-700"
                : "bg-white hover:bg-slate-50 text-slate-700 border-slate-300"
            } disabled:opacity-50`}
          >
            {savingStrict
              ? <Loader2 size={12} className="animate-spin" />
              : strictMode ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
            {strictMode ? "ON — Turn OFF" : "OFF — Turn ON"}
          </button>
        </div>
      </div>

      {/* ── Audit action row ──────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button data-testid="project-audit-run-btn"
                onClick={runAudit} disabled={busy}
                className="bg-indigo-700 hover:bg-indigo-800 disabled:opacity-50 text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
          {busy ? "Scanning…" : "Run Audit"}
        </button>
        <button data-testid="project-audit-xlsx-btn"
                onClick={downloadXlsx} disabled={busy || !audit}
                className="bg-slate-100 hover:bg-slate-200 disabled:opacity-40 text-slate-700 border border-slate-300 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
          <Download size={12} /> Audit Excel
        </button>
        {/* Sprint 2026-Feb-10j · Excel bulk template + upload */}
        <button data-testid="project-audit-template-btn"
                onClick={downloadBulkTemplate}
                title="Download an editable workbook of every Active project — fill in the amber columns and upload back."
                className="bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
          <Download size={12} /> Template
        </button>
        <label
          data-testid="project-audit-upload-label"
          className={`${uploading ? "bg-amber-300 cursor-progress" : "bg-amber-700 hover:bg-amber-800 cursor-pointer"} text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5`}
          title="Upload the edited template. Existing users are preserved by default (append); tick 'Replace Mode?' = Y in the sheet to overwrite.">
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {uploading ? "Uploading…" : "Upload Filled"}
          <input type="file"
                 data-testid="project-audit-upload-input"
                 accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                 className="hidden"
                 disabled={uploading}
                 onChange={handleBulkUpload} />
        </label>
        {audit && (
          <div className="text-[11px] text-slate-600 ml-auto flex gap-3">
            <span data-testid="audit-summary-with">✓ With users: <strong>{audit.summary.with_users}</strong></span>
            <span data-testid="audit-summary-without" className={audit.summary.without_users > 0 ? "text-rose-600" : ""}>
              ✗ Missing: <strong>{audit.summary.without_users}</strong>
            </span>
            <span>Total Active: <strong>{audit.summary.total_active}</strong></span>
          </div>
        )}
      </div>

      {!audit ? (
        <div className="text-[11px] text-slate-400 border border-dashed border-slate-200 px-3 py-4 text-center">
          Click <span className="font-bold">Run Audit</span> to scan the live database.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Projects picker */}
          <div className="border border-slate-200" data-testid="project-audit-project-list">
            <div className="px-2 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                Projects · {visibleProjects.length}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[10px] flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={filterEmpty}
                         onChange={e => setFilterEmpty(e.target.checked)}
                         data-testid="filter-empty-projects" />
                  Only missing
                </label>
                <button onClick={selectAllVisibleProjects}
                        className="text-[10px] uppercase font-bold text-indigo-700 hover:underline">
                  Select all
                </button>
              </div>
            </div>
            <input type="text" placeholder="Search code / name / client / branch…"
                   value={projSearch} onChange={e => setProjSearch(e.target.value)}
                   data-testid="project-audit-search-projects"
                   className="w-full text-[11px] border-b border-slate-200 px-2 py-1.5 focus:outline-none" />
            <div className="max-h-64 overflow-auto">
              {visibleProjects.length === 0 ? (
                <div className="text-[11px] text-slate-400 px-3 py-4 text-center">No projects match.</div>
              ) : visibleProjects.map(p => (
                <label key={p.id}
                       className="flex items-center gap-2 px-2 py-1 border-b border-slate-100 hover:bg-slate-50 cursor-pointer text-[11px]"
                       data-testid={`project-audit-project-${p.project_code}`}>
                  <input type="checkbox"
                         checked={selectedProjectIds.includes(p.id)}
                         onChange={() => toggleProjSelect(p.id)} />
                  <span className="font-mono text-[10px] font-bold text-slate-700">{p.project_code}</span>
                  <span className="text-slate-600 flex-1 truncate">{p.project_name}</span>
                  {p.authorized_user_count > 0 ? (
                    <span className="text-[9px] text-emerald-600 uppercase tracking-wider">
                      {p.authorized_user_count} user{p.authorized_user_count === 1 ? "" : "s"}
                    </span>
                  ) : (
                    <span className="text-[9px] text-rose-600 uppercase tracking-wider">Empty</span>
                  )}
                </label>
              ))}
            </div>
            <div className="px-2 py-1 bg-slate-50 border-t border-slate-200 text-[10px] text-slate-500">
              Selected: <strong className="text-indigo-700">{selectedProjectIds.length}</strong>
            </div>
          </div>

          {/* Users picker */}
          <div className="border border-slate-200" data-testid="project-audit-user-list">
            <div className="px-2 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                Users · {visibleUsers.length}
              </div>
              <button onClick={() => setSelectedUserIds(Array.from(new Set([...selectedUserIds, ...visibleUsers.map(u => u.id)])))}
                      className="text-[10px] uppercase font-bold text-indigo-700 hover:underline">
                Select all
              </button>
            </div>
            <input type="text" placeholder="Search name / email / role…"
                   value={userSearch} onChange={e => setUserSearch(e.target.value)}
                   data-testid="project-audit-search-users"
                   className="w-full text-[11px] border-b border-slate-200 px-2 py-1.5 focus:outline-none" />
            <div className="max-h-64 overflow-auto">
              {visibleUsers.length === 0 ? (
                <div className="text-[11px] text-slate-400 px-3 py-4 text-center">No users match.</div>
              ) : visibleUsers.map(u => (
                <label key={u.id}
                       className="flex items-center gap-2 px-2 py-1 border-b border-slate-100 hover:bg-slate-50 cursor-pointer text-[11px]"
                       data-testid={`project-audit-user-${u.id}`}>
                  <input type="checkbox"
                         checked={selectedUserIds.includes(u.id)}
                         onChange={() => toggleUserSelect(u.id)} />
                  <span className="font-bold text-slate-800 flex-1 truncate">{u.name || u.email}</span>
                  <span className="text-[9px] text-slate-500 uppercase tracking-wider">{u.role || "user"}</span>
                </label>
              ))}
            </div>
            <div className="px-2 py-1 bg-slate-50 border-t border-slate-200 text-[10px] text-slate-500">
              Selected: <strong className="text-indigo-700">{selectedUserIds.length}</strong>
            </div>
          </div>

          {/* Apply row */}
          <div className="md:col-span-2 flex flex-wrap items-center gap-3 border border-slate-200 bg-slate-50 p-2.5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Mode</span>
              <select value={mode} onChange={e => setMode(e.target.value)}
                      data-testid="project-audit-mode-select"
                      className="text-[11px] border border-slate-300 px-2 py-1 bg-white">
                <option value="append">Append (add users, keep existing)</option>
                <option value="replace">Replace (overwrite existing list) — DANGER</option>
              </select>
            </div>
            <div className="text-[11px] text-slate-600">
              Will affect <strong className="text-indigo-700">{selectedProjectIds.length}</strong> project{selectedProjectIds.length === 1 ? "" : "s"}
              {" "} × <strong className="text-indigo-700">{selectedUserIds.length}</strong> user{selectedUserIds.length === 1 ? "" : "s"}
            </div>
            <button data-testid="project-audit-apply-btn"
                    onClick={applyBulk}
                    disabled={applying || selectedProjectIds.length === 0 || selectedUserIds.length === 0}
                    className="ml-auto bg-indigo-700 hover:bg-indigo-800 disabled:opacity-40 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
              {applying ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {applying ? "Applying…" : "Apply Bulk Assign"}
            </button>
            {applyResult && (
              <div className="basis-full mt-2 text-[11px] bg-emerald-50 border border-emerald-200 px-2 py-1.5"
                   data-testid="project-audit-apply-result">
                <div className="inline-flex items-center gap-1 font-bold text-emerald-800">
                  <CheckCircle2 size={12} /> Applied · Mode: <span className="font-mono">{applyResult.mode}</span>
                </div>
                <div className="text-slate-700 mt-0.5">
                  Modified: <strong>{applyResult.modified}</strong> ·
                  Unchanged: <strong>{applyResult.unchanged}</strong> ·
                  User count: <strong>{applyResult.user_count}</strong>
                  {(applyResult.unknown_project_ids?.length > 0) && (
                    <span className="text-rose-600 ml-2">Unknown project ids: {applyResult.unknown_project_ids.length}</span>
                  )}
                  {(applyResult.invalid_user_ids?.length > 0) && (
                    <span className="text-rose-600 ml-2">Invalid user ids: {applyResult.invalid_user_ids.length}</span>
                  )}
                </div>
              </div>
            )}
            {/* Sprint 2026-Feb-10j — Excel upload result summary */}
            {uploadResult && (
              <div className="basis-full mt-2 text-[11px] bg-indigo-50 border border-indigo-200 px-2 py-1.5"
                   data-testid="project-audit-upload-result">
                <div className="inline-flex items-center gap-1 font-bold text-indigo-800">
                  <CheckCircle2 size={12} /> Excel Upload Processed
                </div>
                <div className="text-slate-700 mt-0.5">
                  Projects updated: <strong>{uploadResult.rows_processed}</strong> ·
                  Renamed: <strong>{uploadResult.projects_renamed}</strong> ·
                  Reassigned users: <strong>{uploadResult.projects_reassigned}</strong> ·
                  Skipped (empty rows): <strong>{uploadResult.rows_skipped_empty}</strong>
                </div>
                {uploadResult.unknown_project_codes?.length > 0 && (
                  <div className="text-rose-700 mt-1">
                    Unknown project codes ({uploadResult.unknown_project_codes.length}):{" "}
                    <span className="font-mono">{uploadResult.unknown_project_codes.slice(0, 8).join(", ")}</span>
                    {uploadResult.unknown_project_codes.length > 8 && ` (+${uploadResult.unknown_project_codes.length - 8} more)`}
                  </div>
                )}
                {uploadResult.warnings?.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-amber-700 font-bold">
                      ⚠ {uploadResult.warnings.length} warning{uploadResult.warnings.length === 1 ? "" : "s"} (click to expand)
                    </summary>
                    <ul className="list-disc list-inside text-[10px] text-slate-600 mt-1 max-h-32 overflow-auto">
                      {uploadResult.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}




// ─────────────────────────────────────────────────────────────────
// Sprint 2026-Jun — VoltLedger integration Phase D.
// Reconciliation Detail — STRICTLY READ-ONLY per-invoice drill of the
// receivables reconciliation gate. No repair / recalculation / update
// actions exist here by explicit client mandate; historical fixes are
// deferred to a separate Data Integrity Remediation Sprint.
// ─────────────────────────────────────────────────────────────────
function ReconciliationDetailTile() {
  const [data, setData] = useState(null);
  const [onlyIssues, setOnlyIssues] = useState(true);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 10;
  const inr = (v) => (Number(v) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  async function load(p = page, issues = onlyIssues) {
    setLoading(true);
    try {
      const r = await api.get("/receivables/reconciliation/detail", {
        params: { only_issues: issues, skip: p * LIMIT, limit: LIMIT },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reconciliation detail");
    } finally { setLoading(false); }
  }

  const pages = Math.max(1, Math.ceil((data?.total || 0) / LIMIT));

  return (
    <Card className="p-5 lg:col-span-2" data-testid="reconciliation-detail-tile">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <Search size={14} className="text-orange-500" /> Reconciliation Detail (Receivables)
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Read-only drill of the aging reconciliation gate — per-invoice comparison of the calculated
            outstanding vs Sales Invoice outstanding, receipt allocations and debtor-ledger postings.
            <b> No repair or recalculation actions exist on this page.</b>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="recon-toggle-issues" onClick={() => { const v = !onlyIssues; setOnlyIssues(v); setPage(0); if (data) load(0, v); }}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-600 border border-slate-300 px-3 py-2 hover:bg-slate-50">
            {onlyIssues ? <ToggleRight size={14} className="text-orange-500" /> : <ToggleLeft size={14} />}
            Issues only
          </button>
          <button data-testid="recon-load" onClick={() => load(0)} disabled={loading}
            className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} {data ? "Refresh" : "Load Detail"}
          </button>
        </div>
      </div>

      {data && (
        <>
          <div className="mt-3 flex gap-4 text-xs">
            <span className="text-slate-600">Rows: <b>{data.total}</b></span>
            <span className={data.issues_count ? "text-amber-700 font-bold" : "text-emerald-700 font-bold"}>
              Issues: {data.issues_count}
            </span>
          </div>
          <div className="mt-2 overflow-x-auto border border-slate-200">
            <table className="w-full text-[11px]" data-testid="recon-detail-table">
              <thead>
                <tr className="bg-slate-100 text-slate-600">
                  {["Invoice", "Client", "Project", "Branch", "Inv Date", "Due Date", "Net Payable",
                    "Receipts", "Stored Recd", "CN", "DN", "Retention (P/R)", "Ledger O/S",
                    "Calculated O/S", "SI O/S", "Diff", "Reasons", "Last Updated", "Source"].map((h) => (
                    <th key={h} className="px-2 py-1.5 text-left text-[9px] uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr><td colSpan={19} className="px-3 py-6 text-center text-slate-400">
                    {onlyIssues ? "No reconciliation issues found — everything ties out." : "No approved invoices in scope."}
                  </td></tr>
                )}
                {data.rows.map((r) => (
                  <tr key={r.invoice_id} className={`border-t border-slate-100 ${r.has_issue ? "bg-amber-50/60" : ""}`}>
                    <td className="px-2 py-1.5 font-semibold whitespace-nowrap">{r.invoice_number}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{r.client_name}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap max-w-[140px] truncate" title={r.project_name}>{r.project_name}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{r.branch_name || "—"}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{r.invoice_date}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{r.due_date || "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{inr(r.net_payable)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{inr(r.receipts)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{inr(r.stored_received_amount)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{inr(r.credit_notes)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{inr(r.debit_notes)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{inr(r.retention_pending)} / {inr(r.retention_released)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.ledger_outstanding === null ? <span className="text-rose-500 font-bold">none</span> : inr(r.ledger_outstanding)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-bold">{inr(r.calculated_outstanding)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{inr(r.si_outstanding)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${Math.abs(r.difference) > 0.01 ? "text-rose-600" : "text-emerald-600"}`}>{inr(r.difference)}</td>
                    <td className="px-2 py-1.5 max-w-[220px]">
                      {(r.difference_reasons || []).map((x, i) => (
                        <div key={i} className="text-[10px] text-amber-800">• {x}</div>
                      ))}
                      {!(r.difference_reasons || []).length && <span className="text-emerald-600 text-[10px]">OK</span>}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-slate-400">{(r.last_updated || "").slice(0, 10)}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-slate-400">{r.source_module}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="text-slate-500">Page {page + 1} of {pages}</span>
            <div className="flex gap-1.5">
              <button disabled={page === 0} onClick={() => { const p = page - 1; setPage(p); load(p); }}
                className="px-2.5 py-1 border border-slate-300 disabled:opacity-40 hover:bg-slate-50">Prev</button>
              <button disabled={page + 1 >= pages} onClick={() => { const p = page + 1; setPage(p); load(p); }}
                className="px-2.5 py-1 border border-slate-300 disabled:opacity-40 hover:bg-slate-50">Next</button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────
// Jul-2026 — SI Stock-Out feature-flag toggle.
// Runtime switch for "Approved Sales Invoices consume Inventory
// stock (FIFO) from the selected warehouse". DB override wins over
// the SALES_INVOICE_MOVES_STOCK env default. Previously this flag
// was API-only, so production tenants could never turn it on.
// ─────────────────────────────────────────────────────────────────
function SiStockOutTile() {
  const [flag, setFlag] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/admin/si-stock-out-flag").then((r) => setFlag(r.data)).catch(() => setFlag(null));
  useEffect(() => { load(); }, []);

  async function toggle() {
    if (!flag) return;
    const next = !flag.enabled;
    if (!window.confirm(next
      ? "Turn ON Sales Invoice Stock-Out?\n\nFrom now, APPROVING a Sales Invoice that has Inventory line items will consume stock (FIFO batches) from the invoice's warehouse. Already-approved SIs are NOT touched — re-save (edit → Save) an approved SI to post its stock consumption."
      : "Turn OFF Sales Invoice Stock-Out?\n\nNewly approved Sales Invoices will STOP consuming stock. Existing stock movements are not reversed.")) return;
    setBusy(true);
    try {
      await api.post("/admin/si-stock-out-flag", { enabled: next });
      toast.success(`Sales Invoice Stock-Out ${next ? "ENABLED" : "DISABLED"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the flag");
    } finally { setBusy(false); }
  }

  const on = !!flag?.enabled;
  return (
    <Card className="p-5" data-testid="si-stock-out-tile">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 flex items-center justify-center border ${on ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-100 border-slate-200 text-slate-500"}`}>
            <PackageMinus size={18} />
          </div>
          <div>
            <h3 className="font-display text-base font-black text-slate-900">Sales Invoice Stock-Out</h3>
            <p className="text-[11px] text-slate-500">
              When ON, approving a Sales Invoice with <b>Inventory</b> line items consumes stock
              (FIFO batches) from the invoice's selected warehouse. Editing &amp; re-saving an
              approved SI re-posts its consumption.
            </p>
          </div>
        </div>
        {flag && (
          <span data-testid="si-stock-out-status"
            className={`shrink-0 text-[10px] uppercase tracking-wider font-bold px-2 py-1 border ${on
              ? "bg-emerald-50 border-emerald-300 text-emerald-800"
              : "bg-rose-50 border-rose-300 text-rose-800"}`}>
            {on ? "ON — stock consumed on approval" : "OFF — approvals do NOT move stock"}
          </span>
        )}
      </div>
      {!flag ? (
        <div className="mt-3 text-[11px] text-slate-400">Loading flag status…</div>
      ) : (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-slate-500">
            Source: <b>{flag.db_override === null || flag.db_override === undefined ? "environment default" : "runtime override (set from this screen)"}</b>
            {flag.updated_at ? <> · last changed {String(flag.updated_at).slice(0, 10)} by {flag.updated_by || "—"}</> : null}
          </div>
          <button
            data-testid="si-stock-out-toggle"
            onClick={toggle}
            disabled={busy}
            className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold text-white disabled:opacity-50 ${on
              ? "bg-rose-600 hover:bg-rose-700"
              : "bg-emerald-600 hover:bg-emerald-700"}`}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : (on ? <ToggleLeft size={15} /> : <ToggleRight size={15} />)}
            {on ? "Turn OFF" : "Turn ON"}
          </button>
        </div>
      )}
    </Card>
  );
}
